import {ChangeDetectionStrategy, Component, computed, effect, inject, signal, viewChild} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {FormControl, FormGroup} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs';
import {
  ApplyPriceCoefficientRequest,
  CreateProductRequest,
  ProductSummary,
  ProductType,
  UpdateProductRequest
} from '../../../../../api';
import {ProductFilter, ProductStoreService} from '../../services/product-store.service';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {Search} from '../../../../components/search/search';
import {ElciaDialog} from '../../../../components/dialog/dialog';
import {Loader} from '../../../../components/loader/loader';
import {FilterContainer} from '../../../../components/filter-container/filter-container';
import {ProductGrid} from '../product-grid/product-grid';
import {ProductForm} from '../product-form/product-form';
import {ProductTypeDropdown} from '../product-type-dropdown/product-type-dropdown';
import {ProductFormValue} from '../../models/product-form-value';
import {ConfirmService} from '../../../../services/confirm.service';
import {PriceCoefficientForm} from '../price-coefficient-form/price-coefficient-form';
import {PriceCoefficientFormValue} from '../../models/price-coefficient-form-value';

@Component({
  selector: 'elcia-product-home',
  imports: [TranslatePipe, ElciaButton, Search, ElciaDialog, Loader, FilterContainer, ProductGrid, ProductForm, ProductTypeDropdown, PriceCoefficientForm],
  templateUrl: './product-home.html',
  providers: [ProductStoreService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductHome {
  protected readonly store = inject(ProductStoreService);
  private readonly confirmService = inject(ConfirmService);

  productForm = viewChild<ProductForm>(ProductForm);
  coefficientForm = viewChild<PriceCoefficientForm>(PriceCoefficientForm);
  isLoading = this.store.isLoading;
  isLoadingSelectedProduct = this.store.isLoadingSelectedProduct;
  products = this.store.products;

  protected isFormDisplayed = signal(false);
  protected isFilterDisplayed = signal(false);
  protected isCoefficientDialogDisplayed = signal(false);
  protected selectedProducts = signal<ProductSummary[]>([]);
  protected hasSelectedProducts = computed(() => this.selectedProducts().length > 0);
  private openMode = signal<'create' | 'edit'>('create');

  filterForm = new FormGroup({
    searchTerm: new FormControl('', {nonNullable: true}),
    productTypes: new FormControl<ProductType[] | null>(null),
    legalEntityIds: new FormControl<number[] | null>(null)
  });

  private readonly filterValue = toSignal(
    this.filterForm.valueChanges.pipe(debounceTime(300)),
    {initialValue: this.filterForm.value}
  );

  private readonly syncFilter = effect(() => {
    const form = this.filterValue();
    this.store.filterParams.set({
      searchTerm: form.searchTerm || null,
      productTypes: form.productTypes ?? null,
      legalEntityIds: form.legalEntityIds ?? null,
    } satisfies ProductFilter);
  });

  protected isFormSaving = computed(() =>
    this.store.createProductMutation.isPending() || this.store.updateProductMutation.isPending()
  );
  protected isEditMode = computed(() => this.openMode() === 'edit');

  protected showForm(): void {
    this.store.createProductMutation.reset();
    this.openMode.set('create');
    this.store.selectedProductId.set(null);
    this.isFormDisplayed.set(true);
  }

  protected closeForm(): void {
    this.isFormDisplayed.set(false);
    this.store.selectedProductId.set(null);
  }

  protected onDialogSave(): void {
    this.productForm()?.submit();
  }

  protected toggleFilterDisplayed(): void {
    this.isFilterDisplayed.update(v => !v);
  }

  protected onProductSelect(product: ProductSummary): void {
    this.store.updateProductMutation.reset();
    this.openMode.set('edit');
    this.store.selectedProductId.set(product.id);
  }

  protected onProductDelete(product: ProductSummary): void {
    this.confirmService.confirm({
      title: 'entity.product',
      message: product.nameFr,
      accept: async () => {
        await this.store.deleteProductMutation.mutateAsync(product.id);
      }
    });
  }

  protected deleteProduct(): void {
    const product = this.store.selectedProduct();
    if (!product?.id) return;
    this.confirmService.confirm({
      title: 'entity.product',
      message: product.nameFr,
      accept: async () => {
        await this.store.deleteProductMutation.mutateAsync(product.id);
        this.closeForm();
      }
    });
  }

  protected submit(formValue: ProductFormValue): void {
    const isEdition = this.openMode() === 'edit'
      && formValue.id !== undefined
      && formValue.id !== null
      && formValue.id !== 0;

    if (isEdition) {
      const body: UpdateProductRequest = {
        nameFr: formValue.nameFr,
        nameEn: formValue.nameEn,
        nameNl: formValue.nameNl,
        descriptionFr: formValue.descriptionFr,
        descriptionEn: formValue.descriptionEn,
        descriptionNl: formValue.descriptionNl,
        frequency: formValue.frequency ?? undefined,
        licencePlanId: formValue.licencePlanId,
        featureId: formValue.featureId ?? undefined,
        pluginId: formValue.pluginId ?? undefined,
        productType: formValue.productType,
        unitOfMeasureCode: formValue.unitOfMeasureCode,
        legalEntityProducts: formValue.legalEntityProducts,
        pricePeriods: formValue.pricePeriods,
      };
      this.store.updateProductMutation.mutate({id: formValue.id!, body});
    } else {
      const request: CreateProductRequest = {
        nameFr: formValue.nameFr,
        nameEn: formValue.nameEn,
        nameNl: formValue.nameNl,
        descriptionFr: formValue.descriptionFr,
        descriptionEn: formValue.descriptionEn,
        descriptionNl: formValue.descriptionNl,
        frequency: formValue.frequency ?? undefined,
        licencePlanId: formValue.licencePlanId,
        featureId: formValue.featureId ?? undefined,
        pluginId: formValue.pluginId ?? undefined,
        productType: formValue.productType,
        unitOfMeasureCode: formValue.unitOfMeasureCode,
        legalEntityProducts: formValue.legalEntityProducts,
        pricePeriods: formValue.pricePeriods,
      };
      this.store.createProductMutation.mutate(request);
    }
  }

  protected showCoefficientDialog(): void {
    this.isCoefficientDialogDisplayed.set(true);
  }

  protected closeCoefficientDialog(): void {
    this.isCoefficientDialogDisplayed.set(false);
    this.store.applyPriceCoefficientMutation.reset();
  }

  protected onCoefficientDialogSave(): void {
    this.coefficientForm()?.submit();
  }

  protected async applyCoefficient(value: PriceCoefficientFormValue): Promise<void> {
    const request: ApplyPriceCoefficientRequest = {
      productIds: this.selectedProducts().map(p => p.id),
      coefficient: value.coefficient,
      startDate: value.startDate,
      endDate: value.endDate,
    };
    await this.store.applyPriceCoefficientMutation.mutateAsync(request);
    this.closeCoefficientDialog();
    this.selectedProducts.set([]);
  }

  private readonly closeFormOnSuccess = effect(() => {
    const createSuccess = this.store.createProductMutation.isSuccess();
    const updateSuccess = this.store.updateProductMutation.isSuccess();
    if (createSuccess || updateSuccess) {
      this.closeForm();
    }
  });

  private readonly openFormOnEdit = effect(() => {
    const selectedProduct = this.store.selectedProduct();
    if (!selectedProduct) return;
    this.isFormDisplayed.set(true);
  });
}
